import {
  Button, Card, CardActions, CardContent, CardHeader, FormControl, Paper, Tooltip, Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import {
  getIn, useFormikContext,
} from 'formik';
import React, {
  useEffect,
} from 'react';
import Dropzone from 'react-dropzone';
import FieldError from '../../Utils/FieldError';
import {
  FieldFile, FieldPropsCommon, ImagePickerFieldValue, LocalFile,
} from '../types';
import {
  onChange,
} from '../utils';
import {
  FilePreviews,
} from './constants';
import classes from './styles.module.scss';
import {
  getFileName, getFileType,
} from './utils';

type Props<T, K extends keyof T> = FieldPropsCommon<T, K> & ImagePickerFieldValue<T, K>;

const FilePickerField = <FormModel, FormModelKey extends keyof FormModel>(props: Props<FormModel, FormModelKey>) => {
  const {
    values,
    handleChange,
    isSubmitting,
    setFieldTouched,
    errors,
    touched,
  } = useFormikContext<FormModel>();

  const {
    disabled,
    title,
    location,
  } = props;

  useEffect(() => {
    if (isSubmitting) {
      setFieldTouched(location);
    }
  }, [isSubmitting, location, setFieldTouched]);

  const value: FieldFile[] = getIn(values, location);
  const onValueChange = onChange(handleChange, location);
  const error = getIn(errors, location);
  const isTouched = getIn(touched, location);

  const onDrop = (acceptedFiles: File[]) => {
    const newFiles: LocalFile[] = acceptedFiles.map((file) => ({
      file,
      type: getFileType(file),
    }));
    onValueChange(value.concat(newFiles));
    setFieldTouched(location);
  };

  const onRemoveImage = (index: number) => {
    const imageValues: FieldFile[] = value;
    imageValues.splice(index, 1);
    onValueChange(imageValues);
  };

  const swapImage = (index: number, direction: 'left' | 'right') => {
    const imageValues: FieldFile[] = value;
    const targetIndex = direction === 'left' ? index - 1 : index + 1;
    const temp = imageValues[targetIndex];
    imageValues[targetIndex] = imageValues[index];
    imageValues[index] = temp;
    onValueChange(imageValues);
  };

  return (
    <div
      className={classes.container}
      style={{
        margin: 8,
      }}
    >
      <FormControl>
        <div className={classNames(classes.gallery)}>
          <Typography
            variant="caption"
            gutterBottom
            className={classNames(classes.label)}
          >
            {title}
          </Typography>
          <Dropzone
            onDrop={onDrop}
            disabled={disabled}
          >
            {({
              getRootProps, getInputProps, isDragActive,
            }) => (
              <Paper
                {...getRootProps()}
                className={classNames('dropzone', {
                  'dropzone--isActive': isDragActive,
                })}
                style={{
                  height: 128,
                  margin: 8,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <input {...getInputProps()} onClick={() => setFieldTouched(location)} />
                {
                    isDragActive
                      ? <Typography variant="subtitle1" gutterBottom> Drop files here... </Typography>
                      : (
                        <Typography variant="subtitle1" gutterBottom>Drag and Drop Files here</Typography>
                      )
                  }
              </Paper>
            )}
          </Dropzone>
          <div className={classes.thumbsContainer}>
            {
              value.map((file, index) => {
                const PreviewComponent = FilePreviews[file.type];
                return (
                  <Card
                    style={{
                      position: 'relative',
                      margin: 8,
                    }}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${index}`}
                  >
                    <Tooltip
                      title={getFileName(file)}
                    >
                      <div>
                        <CardHeader
                          title={getFileName(file)}
                          className={classes.filePickerCardHeader}
                        />
                      </div>
                    </Tooltip>
                    <CardContent
                      style={{
                        width: 128,
                        height: 128,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        margin: 'auto',
                      }}
                    >
                      <PreviewComponent {...file} />
                    </CardContent>
                    <CardActions>
                      <Button
                        variant="outlined"
                        disabled={index === 0}
                        onClick={() => swapImage(index, 'left')}
                      >
                        {'<'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => onRemoveImage(index)}
                      >
                        X
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={index === value.length - 1}
                        onClick={() => swapImage(index, 'right')}
                      >
                        {'>'}
                      </Button>
                    </CardActions>
                  </Card>
                );
              })
            }
          </div>
          <FieldError
            errors={error}
            touched={isTouched}
          />
        </div>
      </FormControl>
    </div>
  );
};

export default FilePickerField;
